/* eslint-disable @typescript-eslint/no-explicit-any */
import { useReactiveVar } from '@apollo/client';
import { Sticky } from '@starrez/starryui9';
import { Button } from '@starryui10/Buttons/Button';
import { Fieldset } from '@starryui10/Components/Fieldset';
import { LabelledControl } from '@starryui10/Components/LabelledControl';
import { TextInput } from '@starryui10/Components/TextInput';
import { SelectInput } from '@starryui10/controls/SelectInput';
import { ErrorDisplay } from '@starryui10/Errors/ErrorDisplay';
import { BasicGrid } from '@starryui10/grids/BasicGrid/BasicGrid';
import { useSelectionColumn } from '@starryui10/grids/cells/SelectionColumn/SelectionColumn';
import { HStack, VStack } from '@starryui10/layouts/Stack';
import { Modal } from '@starryui10/modal/Modal';
import { ColDef } from 'ag-grid-community';
import React, { FormEvent, useEffect, useState } from 'react';
import { createRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAllEntryStatus } from '../graphql/useLazyAllEntryStatusQuery';
import { EntryStatusEnum } from '../graphql/useMainDirectoryQuery';
import { IResidentSearchFilter, useResidentData } from '../graphql/useResidentData';
import { strings } from '../localization';
import { showEntrySearchScreen } from '../state/Observables';

export const EntrySearch = () => {
    const { visible, result } = useReactiveVar(showEntrySearchScreen);
    return <>{visible && <EntrySearchModal onComplete={result!} />}</>;
};

const EntrySearchModal = ({ onComplete }: { onComplete: (ids: string[]) => void }) => {
    const [results, setResults] = useState<any[]>([]);
    const call = useResidentData();
    const [selectedAmount, setSelectedAmount] = useState<string[]>([]);
    const { data } = useAllEntryStatus();
    const [error, setError] = useState<any>();
    const ref = createRef<HTMLInputElement>();
    const form = createRef<HTMLFormElement>();
    const { global, security } = useAppContext();

    useEffect(() => {
        ref?.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);

    const handleSelectEntries = () => {
        if (selectedAmount.length === 0) return;

        showEntrySearchScreen({ visible: false });
        onComplete(selectedAmount);
    };

    const Footer = () => (
        <HStack justifyContent="end" gap="small" columns="max-content">
            <Button onClick={handleSelectEntries} variant="Primary">
                {strings.Action_Select}
            </Button>
            <Button
                onClick={() => {
                    showEntrySearchScreen({ visible: false });
                }}
                variant="Secondary"
                type="button"
            >
                {strings.Action_Close}
            </Button>
        </HStack>
    );

    const handleOnSubmit = (e: FormEvent) => {
        e.preventDefault();

        const data = new FormData(e.target as HTMLFormElement);
        const filter: IResidentSearchFilter = { _Operator: 'OR' };

        const search = data.get('search')!.toString();
        const byFields = data.get('byFields')!.toString();
        const status = data.get('status')!.toString();

        const fields = byFields.split(',');
        fields.forEach((e) => {
            switch (e) {
                case 'iD':
                case 'iD4':
                case 'iD5':
                    ((filter as any)[e] as any) = { startWith: search };
                    break;

                default:
                    ((filter as any)[e] as any) = { like: search };
            }
        });

        const statusFilter: IResidentSearchFilter = { _Operator: 'AND' };

        if (status) {
            statusFilter.entryStatus = { eq: status };
        }

        call({ filter: [filter, statusFilter] }, 'network-only')
            .then((result) => {
                setResults(result.data.allResident.nodes);
            })
            .catch((e) => {
                setError(e);
            });
    };

    const selectionCell = useSelectionColumn((selected, data) => {
        if (selected) {
            selectedAmount.push(data.id);
            setSelectedAmount(selectedAmount);
        } else {
            const itemIndex = selectedAmount.findIndex((e) => e === data.id);
            selectedAmount.slice(itemIndex, 1);
        }
    });

    const showStatus = (e: EntryStatusEnum) => {
        switch (e) {
            case 'ERASED':
            case 'CANCELLED':
            case 'MASTER_ACCOUNT':
            case 'HISTORY':
                return false;

            default:
                return true;
        }
    };

    const columns: ColDef[] = [
        {
            ...selectionCell,
            field: 'selected',
        },
        {
            headerName: 'Last Name',
            field: 'nameLast',
            width: 100,
        },
        {
            headerName: 'First Name',
            field: 'nameFirst',
            width: 100,
        },
        security.entry.field.iD1 && {
            headerName: global?.setting?.iD1Label,
            field: 'iD1',
            width: 60,
        },
        security.entry.field.iD2 && {
            headerName: global?.setting?.iD2Label,
            field: 'iD2',
            width: 60,
        },
        security.entry.field.iD3 && {
            headerName: global?.setting?.iD3Label,
            field: 'iD3',
            width: 60,
        },
        security.entry.field.iD4 && {
            headerName: global?.setting?.iD4Label,
            field: 'iD4',
            width: 60,
        },
        security.entry.field.iD5 && {
            headerName: global?.setting?.iD5Label,
            field: 'iD5',
            width: 60,
        },
        {
            headerName: 'Status',
            field: 'entryStatus.abbreviation',
            width: 80,
        },
        {
            headerName: 'ID',
            field: 'id',
            width: 80,
        },
    ];

    return (
        <Modal
            footer={<Footer />}
            variant="Medium"
            title={strings.EntrySearch_Header}
            height="90vh"
            onClose={() => showEntrySearchScreen({ visible: false })}
        >
            <VStack pad="medium">
                <Sticky top="0" background="white">
                    <form ref={form} onSubmit={handleOnSubmit}>
                        <Fieldset caption={strings.EntrySearch_SearchOptions}>
                            <VStack pad="small" gap="small">
                                <LabelledControl variant="SmallColumns" caption={strings.EntrySearch_Search} htmlFor="Search">
                                    <TextInput ref={ref} name="search" id="search" />
                                </LabelledControl>

                                <LabelledControl variant="SmallColumns" caption={strings.EntrySearch_ByFields} htmlFor="byFields">
                                    <SelectInput name="byFields" id="byFields">
                                        <option value={['nameLast', 'nameFirst', 'iD1']}>
                                            {strings.formatString(strings.EntrySearch_NameRoomID1, global.setting.iD1Label)}
                                        </option>
                                        <option value={['iD1', 'iD2', 'iD3', 'iD4', 'iD5']}>{strings.EntrySearch_AllIDFields}</option>
                                        <option value={['nameLast', 'nameFirst']}>{strings.EntrySearch_EntryName}</option>
                                        <option value={['iD1']}>{global?.setting?.iD1Label}</option>
                                        {/* <option value="RoomSpace_Description">Room Space Description</option> */}
                                        {/* <option value="Category_Description">Category Description</option> */}
                                        <option value={['iD']}>{strings.EntrySearch_EntryID}</option>
                                        <option value={['nameLast', 'nameFirst', 'iD1', 'iD2', 'iD3', 'iD4', 'iD5']}>
                                            {strings.EntrySearch_AllFields}
                                        </option>
                                    </SelectInput>
                                </LabelledControl>

                                <LabelledControl variant="SmallColumns" caption={strings.EntrySearch_ByStatus} htmlFor="status">
                                    <SelectInput name="status" id="status">
                                        <option value="">{strings.Label_AllEntries}</option>
                                        {data?.allEntryStatus
                                            ?.filter((e) => showStatus(e.enumValue))
                                            .map((e) => (
                                                <option key={e.id} value={e.id}>
                                                    {e.description}
                                                </option>
                                            ))}
                                    </SelectInput>
                                </LabelledControl>

                                <div style={{ paddingLeft: '90px' }}>
                                    <Button type="submit" compact variant="Primary">
                                        {strings.Action_Search}
                                    </Button>
                                </div>
                            </VStack>
                        </Fieldset>
                    </form>
                </Sticky>

                {error && <ErrorDisplay error={error} />}

                <div>
                    <BasicGrid headerHeight={32} rowHeight={40} columnDefs={columns.filter((e) => e)} domLayout="autoHeight" rowData={results} />
                </div>
            </VStack>
        </Modal>
    );
};
